import React, { useEffect, useState } from "react";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import VolunteerGroupModal from "./VolunteerGroupModal";
import VolunteerGroupList from "./VolunteerGroupList";
import { getAuthHeaders } from "../../../api/authHelper";
import "./VolunteerGroup.css";

import Billede1 from "../../../Images/Billede1.jpg";

interface FunctionDto {
    functionId: number;
    functionName: string;
    eventID: number;
}

interface VolunteerGroup {
    id?: number;
    name: string;
    type: string;

    eventId: number;
    event?: {
        eventID: number;
        eventName: string;
    };

    function?: string;
    maxSlots: number;

    contactPersonId?: number;
    contactPerson?: {
        id: number;
        firstName: string;
        lastName: string;
    };

    selfAssignShifts: boolean;
    deadline?: string;
}

interface EventDto {
    eventID: number;
    eventName: string;
}

const VolunteerGroups: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL!;
    const [functions, setFunctions] = useState<FunctionDto[]>([]);
    const [groups, setGroups] = useState<VolunteerGroup[]>([]);
    const [events, setEvents] = useState<EventDto[]>([]);
    const [persons, setPersons] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState<VolunteerGroup>({
        name: "",
        type: "",
        eventId: 0,
        function: "",
        maxSlots: 0,
        contactPersonId: undefined,
        selfAssignShifts: false,
        deadline: "",
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [groupsRes, eventsRes, personsRes, functionsRes] = await Promise.all([
                fetch(`${apiUrl}/api/VolunteerGroups`, { headers: getAuthHeaders() }),
                fetch(`${apiUrl}/api/events`, { headers: getAuthHeaders() }),
                fetch(`${apiUrl}/api/VolunteerGroups/all-persons`, {
                    headers: getAuthHeaders()
                }),
                fetch(`${apiUrl}/api/Function`, { headers: getAuthHeaders() }),
            ]);

            const groupsData = await groupsRes.json();
            const eventsData = await eventsRes.json();
            const personsData = await personsRes.json();
            const functionsData = await functionsRes.json();

            const groupsList = groupsData?.$values ?? groupsData ?? [];
            const eventsList = eventsData?.$values ?? eventsData ?? [];
            const functionsList = functionsData?.$values ?? functionsData ?? [];

            // persons endpoint returns ARRAY (no paging wrapper)
            const personsList = Array.isArray(personsData)
                ? personsData
                : personsData?.items ?? personsData?.$values ?? [];

            setGroups(groupsList);
            setEvents(eventsList);
            setFunctions(functionsList);
            setPersons(personsList);

        } catch (err) {
            console.error("loadData error:", err);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setForm({
            name: "",
            type: "",
            eventId: 0,
            function: "",
            maxSlots: 0,
            contactPersonId: undefined,
            selfAssignShifts: false,
            deadline: "",
        });
    };

    const saveGroup = async () => {
        try {
            setSaving(true);
            setSaveError(null);

            const isEdit = editingId !== null;

            const url = isEdit
                ? `${apiUrl}/api/VolunteerGroups/${editingId}`
                : `${apiUrl}/api/VolunteerGroups`;

            const method = isEdit ? "PUT" : "POST";

            const payload = {
                name: form.name.trim(),
                type: form.type,
                eventId: form.eventId,
                function: form.function || null,
                maxSlots: form.maxSlots,
                contactPersonId: form.contactPersonId ?? null,
                selfAssignShifts: form.selfAssignShifts,

                // Tom deadline skal være null
                deadline: form.deadline
                    ? form.deadline
                    : null,
            };

            const res = await fetch(url, {
                method,
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                let message = "Gruppen kunne ikke gemmes.";

                try {
                    const contentType = res.headers.get("content-type");

                    if (contentType?.includes("application/json")) {
                        const errorData = await res.json();

                        console.error("VolunteerGroup API error:", errorData);

                        // -----------------------------------------
                        // BACKEND MESSAGE
                        // -----------------------------------------

                        if (errorData?.message) {
                            const backendMessage = String(errorData.message);

                            if (
                                backendMessage
                                    .toLowerCase()
                                    .includes("already exists")
                            ) {
                                message =
                                    `En frivilliggruppe med navnet "${form.name}" findes allerede.`;
                            } else {
                                message = backendMessage;
                            }
                        }

                        // -----------------------------------------
                        // ASP.NET VALIDATION ERRORS
                        // -----------------------------------------

                        if (errorData?.errors) {
                            const validationMessages: string[] = [];

                            Object.entries(errorData.errors).forEach(
                                ([field, messages]) => {
                                    const errorMessages = Array.isArray(messages)
                                        ? messages
                                        : [messages];

                                    errorMessages.forEach((errorMessage) => {
                                        const text = String(errorMessage);

                                        if (
                                            field
                                                .toLowerCase()
                                                .includes("deadline") ||
                                            text
                                                .toLowerCase()
                                                .includes("datetime")
                                        ) {
                                            validationMessages.push(
                                                "Deadline har et ugyldigt format."
                                            );

                                            return;
                                        }

                                        // "The dto field is required" er typisk
                                        // bare følgefejl fra ASP.NET model binding.
                                        if (
                                            field.toLowerCase() === "dto"
                                        ) {
                                            return;
                                        }

                                        validationMessages.push(text);
                                    });
                                }
                            );

                            if (validationMessages.length > 0) {
                                message = [
                                    ...new Set(validationMessages),
                                ].join("\n");
                            }
                        }
                    } else {
                        const text = await res.text();

                        console.error("VolunteerGroup API error:", text);

                        if (
                            text
                                .toLowerCase()
                                .includes("already exists")
                        ) {
                            message =
                                `En frivilliggruppe med navnet "${form.name}" findes allerede.`;
                        } else if (text) {
                            message = text;
                        }
                    }
                } catch (parseError) {
                    console.error(
                        "Could not parse API error:",
                        parseError
                    );
                }

                setSaveError(message);
                return;
            }

            // ---------------------------------------------
            // CREATE PERSONS
            // ---------------------------------------------

            if (!isEdit) {
                const createdGroup = await res.json();

                const generateRes = await fetch(
                    `${apiUrl}/api/VolunteerGroups/${createdGroup.id}/generate-persons`,
                    {
                        method: "POST",
                        headers: getAuthHeaders(),
                    }
                );

                if (!generateRes.ok) {
                    console.error(
                        "Group created, but persons could not be generated."
                    );
                }
            }

            await loadData();

            resetForm();

            setShowModal(false);
            setSaveError(null);
        } catch (err) {
            console.error(err);

            setSaveError(
                "Der opstod en uventet fejl. Prøv igen."
            );
        } finally {
            setSaving(false);
        }
    };

    const editGroup = (group: VolunteerGroup) => {
        setSaveError(null);
        setEditingId(group.id ?? null);

        setForm({
            name: group.name,
            type: group.type,
            eventId: group.eventId,
            function: group.function ?? "",
            maxSlots: group.maxSlots,
            contactPersonId: group.contactPersonId,
            selfAssignShifts: group.selfAssignShifts,
            deadline: group.deadline ?? "",
        });

        setShowModal(true);
    };

    const deleteGroup = async (id?: number) => {
        if (!id) return;
        if (!window.confirm("Slet gruppe?")) return;

        await fetch(`${apiUrl}/api/VolunteerGroups/${id}`, {
            method: "DELETE",
            headers: getAuthHeaders(),
        });

        await loadData();
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Volunteer Groups"
                titleDa="Frivilliggrupper"
                descriptionEn="Manage volunteer groups and assignments"
                descriptionDa="Opret, redigér og administrér frivilliggrupper"
                imageSrc={Billede1}
            />

            <div className="persons-container">

                <button
                    className="edit-btn"
                    onClick={() => {
                        resetForm();
                        setSaveError(null);
                        setShowModal(true);
                    }}
                >
                    + Opret gruppe
                </button>

                <VolunteerGroupModal
                    open={showModal}
                    form={form}
                    events={events}
                    persons={persons}
                    functions={functions}
                    editingId={editingId}
                    setForm={setForm}
                    onClose={() => {
                        setShowModal(false);
                        setSaveError(null);
                    }}
                    onSave={saveGroup}
                    saveError={saveError}
                    saving={saving}
                />

                <VolunteerGroupList
                    groups={groups}
                    persons={persons}
                    onEdit={editGroup}
                    onDelete={deleteGroup}
                />
            </div>
        </div>
    );
};

export default VolunteerGroups;